import type { SortSpec } from '@shared/filters';
import type { SavedViewConfig } from '@shared/models';
import type { SavedViewDto } from '@shared/views';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Check, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Spinner } from '../../components/ui/feedback';
import { Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';

export interface SavedViewsProps {
  projectId: string | undefined;
  /** The state a "Save current view" click should capture. */
  current: SavedViewConfig;
  onApply: (config: SavedViewConfig) => void;
}

/**
 * Saved views: name the way you are looking at the table, then get back to it in one click.
 *
 * A view stores the filter, search, ordering and status together, because those four are what
 * make a result set mean something — restoring three of them would land you somewhere else.
 */
export function SavedViews({ projectId, current, onApply }: SavedViewsProps) {
  const client = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [appliedId, setAppliedId] = useState<string | null>(null);

  const views = useQuery({
    queryKey: queryKeys.savedViews(projectId),
    queryFn: () => api.get<SavedViewDto[]>(`/api/views${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`),
  });

  const save = useMutation({
    mutationFn: () =>
      api.post<SavedViewDto>('/api/views', {
        name: name.trim(),
        ...(projectId ? { projectId } : {}),
        config: current,
      }),
    onSuccess: async (view) => {
      setNaming(false);
      setName('');
      setAppliedId(view.id);
      await client.invalidateQueries({ queryKey: ['views'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/views/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['views'] });
    },
  });

  const apply = (view: SavedViewDto): void => {
    setAppliedId(view.id);
    onApply(view.config);
  };

  const list = views.data ?? [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1 text-[13px] text-muted-foreground">
          <Bookmark className="h-3.5 w-3.5" aria-hidden="true" />
          Views
        </span>

        {views.isPending ? <Spinner className="h-3 w-3" /> : null}

        {list.map((view) => (
          <span
            key={view.id}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] transition-colors duration-150 ${
              appliedId === view.id ? 'border-accent bg-accent-subtle text-accent' : 'border-border text-muted-foreground'
            }`}
          >
            <button type="button" className="hover:text-accent" onClick={() => apply(view)}>
              {appliedId === view.id ? <Check className="mr-0.5 inline h-3 w-3" aria-hidden="true" /> : null}
              {view.name}
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Delete the view "${view.name}"`}
              onClick={() => remove.mutate(view.id)}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}

        {!naming ? (
          <Button size="sm" variant="ghost" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setNaming(true)}>
            Save current view
          </Button>
        ) : null}
      </div>

      {naming ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="h-8 w-56 text-[13px]"
            value={name}
            autoFocus
            placeholder="Name this view"
            aria-label="View name"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && name.trim()) save.mutate();
              if (event.key === 'Escape') setNaming(false);
            }}
          />
          <Button size="sm" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setNaming(false);
              setName('');
            }}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {save.error ? <Alert tone="error">{errorMessage(save.error)}</Alert> : null}
    </div>
  );
}

/** Build the stored shape from the table's live state. */
export function toSavedViewConfig(input: {
  filter: SavedViewConfig['filter'];
  search: string;
  sort: SortSpec[];
  status: 'active' | 'discarded' | 'all';
}): SavedViewConfig {
  return {
    columns: [],
    sort: input.sort,
    ...(input.filter ? { filter: input.filter } : {}),
    ...(input.search.trim() ? { search: input.search.trim() } : {}),
    status: input.status,
  };
}
